'use client'


import type {
    AboutAirline,
    AirlineAwards,
    AirlineBooking,
    AirlineNews,
    FAQSection,
    GeneralInformation,
    PopularAirlineDestinations
} from '@/src/types/airlineDetail'
import { TranslationFunction } from '@/src/types/translations'
import { getFlightLogo } from '@/src/utils/confirmation/airlineUtils'
import { useHandleTopCityBookNow } from '@/src/utils/navigation/handleTopCityBookNow'
import { ExpandMore, FlightTakeoff, Star, StarBorder } from '@mui/icons-material'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Link,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Tabs,
    Typography
} from '@mui/material'
import React from 'react'
import RichText from '@/src/components/common/RichText'

interface AirlineDetailProps {
    isError: boolean
    errorMessage?: string
    hero?: {
        title: string
        rating?: {
            value: number
            max: number
            votes: number
        }
    }
    code?: string
    longName?: string
    aboutAirline?: AboutAirline
    frequentFlightRoutes?: {
        title?: string
        items: Array<{
            from: { city: string; code: string }
            to: { city: string; code: string }
        }>
    }
    airlineNews?: AirlineNews
    airlineBooking?: AirlineBooking
    airlineAwards?: AirlineAwards[]
    airlineHighlights?: {
        title?: string
        tabs: Array<{
            key: string
            label: string
            sections: Array<{
                heading?: string
                content?: string[]
                tableData?: {
                    columns: string[]
                    rows: Record<string, string>[]
                }
            }>
        }>
    }
    generalInformation?: GeneralInformation
    popularAirlineDestinations?: PopularAirlineDestinations
    faq?: FAQSection
    historicMilestones?: {
        title?: string
        milestones: Array<{
            year: string
            title: string
            description: string[]
        }>
    }
    activeTab?: string
    showAllRoutes?: boolean
    onTabChange?: (tab: string) => void
    onToggleRoutes?: () => void
    isMobile?: boolean
    locale: string
    t: TranslationFunction<'airlinePages'>
}

const AirlineDetail: React.FC<AirlineDetailProps> = ({
    isError,
    errorMessage,
    hero,
    code,
    longName,
    aboutAirline,
    frequentFlightRoutes,
    airlineNews,
    airlineBooking,
    airlineAwards,
    airlineHighlights,
    generalInformation,
    popularAirlineDestinations,
    faq,
    historicMilestones,
    activeTab,
    showAllRoutes,
    onTabChange,
    onToggleRoutes,
    isMobile,
    locale,
    t
}) => {
    const { handleBookNow } = useHandleTopCityBookNow()
    // Helper function to convert airline name to URL slug
    const getAirlineSlug = (airlineName: string): string => {
        return airlineName
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^a-zA-Z0-9-]/g, '')
    }

    // Error state
    if (isError) {
        return (
            <Container maxWidth="xl" sx={{ py: 8, textAlign: 'center' }}>
                <Typography component="h1" variant="h5" color="error" gutterBottom>
                    {t('infoNotAvailable')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    {t('tryAgainOrSelectOther')}
                </Typography>
            </Container>
        )
    }

    // Render stars for rating
    const renderStars = (ratingValue: number, max: number = 5) => {
        const stars = []
        for (let i = 0; i < max; i++) {
            stars.push(
                i < ratingValue ? (
                    <Star key={i} sx={{ color: 'warning.main', fontSize: 20 }} />
                ) : (
                    <StarBorder key={i} sx={{ color: 'grey.400', fontSize: 20 }} />
                )
            )
        }
        return stars
    }

    const displayName = longName || hero?.title || ''
    const rating = hero?.rating

    return (
        <Box component="section" sx={{ py: { xs: 2, md: 4 }, bgcolor: 'grey.50' }}>
            <Container maxWidth="xl">
                <Card
                    component="article"
                    sx={{
                        boxShadow: 'none',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden'
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                        {/* 1. Hero Section with Schema.org markup */}
                        <Box
                            component="section"
                            itemScope
                            itemType="https://schema.org/Airline"
                            sx={{
                                bgcolor: 'transparent',
                                borderRadius: 2,
                                p: { xs: 2, md: 3 },
                                mb: { xs: 3, md: 4 },
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            {code && <meta itemProp="iataCode" content={code} />}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, md: 3 }, flexDirection: { xs: 'column', md: 'row' } }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        component="h1"
                                        variant={isMobile ? 'h5' : 'h4'}
                                        sx={{ color: 'text.primary', fontWeight: 700, mb: 2, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
                                        itemProp="name"
                                    >
                                        <strong>{displayName}</strong>
                                    </Typography>
                                    {rating && rating.value > 0 && (
                                        <Box
                                            itemProp="aggregateRating"
                                            itemScope
                                            itemType="https://schema.org/AggregateRating"
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <meta itemProp="ratingValue" content={rating.value.toString()} />
                                                <meta itemProp="bestRating" content={rating.max.toString()} />
                                                <meta itemProp="ratingCount" content={rating.votes.toString()} />
                                                <Typography component="strong" variant="body2" sx={{ color: 'text.primary', fontWeight: 700, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                                    {rating.value}/{rating.max}
                                                </Typography>
                                                {renderStars(rating.value, rating.max)}
                                            </Box>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                                {t('votesCta', { vote: rating.votes })}
                                            </Typography>
                                        </Box>
                                    )}
                                    {code && (
                                        <Chip
                                            label={`${t('code')}: ${code}`}
                                            sx={{
                                                bgcolor: 'grey.100',
                                                color: 'text.primary',
                                                fontWeight: 600,
                                                fontSize: { xs: '0.75rem', md: '0.875rem' }
                                            }}
                                        />
                                    )}
                                </Box>
                                {code && (
                                    <Box
                                        component="img"
                                        src={getFlightLogo(code)}
                                        alt={displayName || 'Airline logo'}
                                        itemProp="logo"
                                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                            const target = e.target as HTMLImageElement
                                            target.src = 'https://ui.cltp.co/images/logos/air-logos/DEFAULT_2x.png'
                                        }}
                                        sx={{
                                            width: { xs: 80, md: 120 },
                                            height: { xs: 80, md: 120 },
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            flexShrink: 0
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* 2. Frequent Flight Routes */}
                        {frequentFlightRoutes && frequentFlightRoutes.items && frequentFlightRoutes.items.length > 0 && (
                            <Box component="section" sx={{ mb: { xs: 3, md: 4 } }}>
                                {frequentFlightRoutes.title && (
                                    <Typography
                                        component="h2"
                                        variant={isMobile ? 'h6' : 'h5'}
                                        sx={{
                                            fontWeight: 700,
                                            mb: { xs: 2, md: 3 },
                                            pb: { xs: 1.5, md: 2 },
                                            borderBottom: 2,
                                            borderColor: 'primary.main',
                                            fontSize: { xs: '1.125rem', md: '1.5rem' }
                                        }}
                                    >
                                        {frequentFlightRoutes.title}
                                    </Typography>
                                )}
                                {(() => {
                                    // For mobile: show only 4 items initially, all items if showAllRoutes is true
                                    // For desktop: always show all items
                                    const displayItems = isMobile && !showAllRoutes
                                        ? frequentFlightRoutes.items.slice(0, 4)
                                        : frequentFlightRoutes.items
                                    const hasMoreThanFour = frequentFlightRoutes.items.length > 4

                                    return (
                                        <>
                                            <Box
                                                component="ul"
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                                    gap: 2,
                                                    mb: { xs: hasMoreThanFour && isMobile ? 2 : 3, md: 4 },
                                                    listStyle: 'none',
                                                    p: 0,
                                                    m: 0
                                                }}
                                            >
                                                {displayItems.map((route, index) => (
                                                    <Box key={index} component="li">
                                                        <Box
                                                            component="div"
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: { xs: 'column', md: 'row' },
                                                                alignItems: { xs: 'stretch', md: 'center' },
                                                                justifyContent: { xs: 'flex-start', md: 'space-between' },
                                                                p: { xs: 2, md: 2 },
                                                                bgcolor: 'grey.50',
                                                                borderRadius: 1,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                gap: { xs: 1.5, md: 0 }
                                                            }}
                                                        >
                                                            {/* Route info row */}
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: { xs: 1, md: 2 },
                                                                justifyContent: { xs: 'center', md: 'flex-start' },
                                                                flex: { xs: 'none', md: 1 },
                                                                minWidth: 0,
                                                                flexWrap: 'wrap'
                                                            }}>
                                                                <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>
                                                                    {`${route.from.city} (${route.from.code})`}
                                                                </Typography>
                                                                <FlightTakeoff sx={{ color: 'grey.400', fontSize: isMobile ? 18 : 20, flexShrink: 0 }} />
                                                                <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>
                                                                    {`${route.to.city} (${route.to.code})`}
                                                                </Typography>
                                                            </Box>

                                                            {/* Book Now button */}
                                                            <Button
                                                                variant="customVariantPrimary"
                                                                onClick={() => handleBookNow(route.from.city, route.to.city, route.from.code, route.to.code)}
                                                                sx={{
                                                                    bgcolor: 'primary.main',
                                                                    color: 'common.white',
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    px: { xs: 2, md: 3 },
                                                                    flexShrink: 0,
                                                                    ml: { xs: 0, md: 2 },
                                                                    width: { xs: '100%', md: 'auto' },
                                                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                                                    '&:hover': {
                                                                        bgcolor: 'primary.dark'
                                                                    }
                                                                }}
                                                            >
                                                                {t('bookNow')}
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>

                                            {/* Show more/less button - Mobile only, only if more than 4 items */}
                                            {isMobile && hasMoreThanFour && (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={onToggleRoutes}
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            px: 3,
                                                            borderColor: 'primary.main',
                                                            color: 'primary.main',
                                                            '&:hover': {
                                                                borderColor: 'primary.dark',
                                                                bgcolor: 'primary.light',
                                                                color: 'primary.dark'
                                                            }
                                                        }}
                                                    >
                                                        {showAllRoutes ? 'Show less' : 'Show more'}
                                                    </Button>
                                                </Box>
                                            )}
                                        </>
                                    )
                                })()}
                                <Divider sx={{ my: { xs: 3, md: 4 } }} />
                            </Box>
                        )}

                        {/* 3. About Section */}
                        {aboutAirline && aboutAirline.sections && aboutAirline.sections.length > 0 && (
                            <Box component="section" sx={{ mb: { xs: 3, md: 4 } }}>
                                <Typography
                                    component="h2"
                                    variant={isMobile ? 'h6' : 'h5'}
                                    sx={{
                                        fontWeight: 700,
                                        mb: { xs: 2, md: 3 },
                                        pb: { xs: 1.5, md: 2 },
                                        borderBottom: 2,
                                        borderColor: 'primary.main',
                                        fontSize: { xs: '1.125rem', md: '1.5rem' }
                                    }}
                                >
                                    {displayName}
                                </Typography>
                                {aboutAirline.sections.map((section, index) => (
                                    <Box key={index} component="div" sx={{ mb: { xs: 2, md: 3 } }}>
                                        {section.heading && (
                                            <Typography component="h3" variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                                                {section.heading}
                                            </Typography>
                                        )}
                                        {section.content && section.content.length > 0 && (
                                            <Box component="div">
                                                {section.content.map((paragraph, pIndex) => (
                                                    <RichText
                                                        key={pIndex}
                                                        html={paragraph}
                                                        component="div"
                                                        variant="body1"
                                                        color="text.secondary"
                                                        sx={{ mb: 2, lineHeight: 1.8, fontSize: { xs: '0.875rem', md: '1rem' } }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                                <Divider sx={{ my: { xs: 3, md: 4 } }} />
                            </Box>
                        )}

                        {/* 4. News Section */}
                        {airlineNews && airlineNews.items && airlineNews.items.length > 0 && (
                            <Box component="section" sx={{ mb: { xs: 3, md: 4 } }}>
                                {airlineNews.title && (
                                    <Typography
                                        component="h2"
                                        variant={isMobile ? 'h6' : 'h5'}
                                        sx={{
                                            fontWeight: 700,
                                            mb: { xs: 2, md: 3 },
                                            pb: { xs: 1.5, md: 2 },
                                            borderBottom: 2,
                                            borderColor: 'primary.main',
                                            fontSize: { xs: '1.125rem', md: '1.5rem' }
                                        }}
                                    >
                                        {airlineNews.title}
                                    </Typography>
                                )}
                                <Box component="div" sx={{ mb: { xs: 3, md: 4 } }}>
                                    {airlineNews.items.map((newsItem, index) => (
                                        <Card key={index} component="article" sx={{ mb: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider' }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                                <Typography component="h3" variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                                                    {newsItem.headline}
                                                </Typography>
                                                {newsItem.date && (
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                                        {newsItem.date}
                                                    </Typography>
                                                )}
                                                {newsItem.content && newsItem.content.length > 0 && (
                                                    <Box component="div">
                                                        {newsItem.content.map((paragraph, pIndex) => (
                                                            <RichText
                                                                key={pIndex}
                                                                html={paragraph}
                                                                component="div"
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{ mb: 1.5, lineHeight: 1.8, fontSize: { xs: '0.875rem', md: '0.875rem' } }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                                <Divider sx={{ my: { xs: 3, md: 4 } }} />
                            </Box>
                        )}

                        {/* Airline Booking Section */}

                        {airlineBooking && airlineBooking?.columns.length > 0 && (
                            <Box component="section" sx={{ mb: { xs: 3, md: 4 } }}>
                                <Typography
                                    component="h2"
                                    variant={isMobile ? 'h6' : 'h5'}
                                    sx={{
                                        fontWeight: 700,
                                        mb: { xs: 2, md: 3 },
                                        pb: { xs: 1.5, md: 2 },
                                        borderBottom: 2,
                                        borderColor: 'primary.main',
                                        fontSize: { xs: '1.125rem', md: '1.5rem' }
                                    }}
                                >
                                    {t('airlineBookings')}
                                </Typography>
                                <Box sx={{ overflowX: 'auto', mb: { xs: 3, md: 4 } }}>
                                    <Table sx={{ minWidth: 300 }}>
                                        <TableBody>
                                            {airlineBooking.rows.map((row, rowIndex) => (
                                                <TableRow key={rowIndex}>
                                                    {airlineBooking.columns.map((column, colIndex) => (
                                                        <TableCell
                                                            key={colIndex}
                                                            sx={{
                                                                fontWeight: colIndex === 0 ? 600 : 400,
                                                                width:
                                                                    airlineBooking.columns.length === 2
                                                                        ? colIndex === 0
                                                                            ? { xs: '50%', md: '40%' }
                                                                            : { xs: '50%', md: '60%' }
                                                                        : 'auto',
                                                                border: 1,
                                                                borderColor: 'grey.200',
                                                                fontSize: { xs: '0.875rem', md: '1rem' },
                                                                py: { xs: 1, md: 1.5 }
                                                            }}
                                                        >
                                                            {row[column]}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                                <Divider sx={{ my: { xs: 3, md: 4 } }} />
                            </Box>
                        )}

                        {/* Awards Section */}
                        {airlineAwards && airlineAwards.length > 0 && (
                            <Box component="section" >
                                <Typography
                                    component="h2"
                                    variant={isMobile ? 'h6' : 'h5'}
                                    sx={{
                                        fontWeight: 700,
                                        mb: { xs: 2, md: 3 },
                                        pb: { xs: 1.5, md: 2 },
                                        borderBottom: 2,
                                        borderColor: 'primary.main',
                                        fontSize: { xs: '1.125rem', md: '1.5rem' }
                                    }}
                                >
                                    {t('airlineAwards')}
                                </Typography>
                                <Box component="div" sx={{ mb: { xs: 1, md: 1.5 } }}>
                                    {airlineAwards.map((awardItem, index) => (
                                        <Card key={index} component="article" sx={{ mb: { xs: 1, md: 1.5 }, border: '1px solid', borderColor: 'divider' }}>
                                            <CardContent sx={{ p: 2 }}>
                                                <Typography component="h3" variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                                                    {awardItem.year}
                                                </Typography>
                                                {awardItem.description && (
                                                    <Box component="div">
                                                        <Typography
                                                            component="div"
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ lineHeight: 1.8, fontSize: { xs: '0.875rem', md: '0.875rem' } }}
                                                        >
                                                            {`${awardItem.title} ${awardItem.description}`}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                                <Divider sx={{ my: { xs: 3, md: 4 } }} />
                            </Box>
                        )}

                        {/* 5. Highlights Section with Tabs (Desktop) / Accordion (Mobile) */}
                        {airlineHighlights && airlineHighlights.tabs && airlineHighlights.tabs.length > 0 && (
                            <Box component="section" sx={{ mb: { xs: 3, md: 4 } }}>
                                {airlineHighlights.title && (
                                    <Typography
                                        component="h2"
                                        variant={isMobile ? 'h6' : 'h5'}
                                        sx={{
                                            fontWeight: 700,
                                            mb: { xs: 2, md: 3 },
                                            pb: { xs: 1.5, md: 2 },
                                            borderBottom: 2,
                                            borderColor: 'primary.main',
                                            fontSize: { xs: '1.125rem', md: '1.5rem' }
                                        }}
                                    >
                                        {airlineHighlights.title}
                                    </Typography>
                                )}
                                {isMobile ? (
                                    // Mobile: Accordion style (like FAQ)
                                    <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                        {airlineHighlights.tabs.map((tab, tabIndex) => {
                                            const panelId = `highlight-panel-${tabIndex}`
                                            const isExpanded = activeTab === tab.key

                                            return (
                                                <Accordion
                                                    key={tab.key}
                                                    expanded={isExpanded}
                                                    onChange={(e, expanded) => onTabChange?.(expanded ? tab.key : '')}
                                                    elevation={0}
                                                    sx={{
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        borderRadius: 2,
                                                        mb: 1.5,
                                                        '&:before': {
                                                            display: 'none'
                                                        },
                                                        '&.Mui-expanded': {
                                                            margin: '0 0 12px 0'
                                                        }
                                                    }}
                                                >
                                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                                            {tab.label}
                                                        </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                                                        <Divider sx={{ mb: 2 }} />
                                                        {tab.sections.map((section, sIndex) => (
                                                            <Box key={sIndex} sx={{ mb: { xs: 2, md: 3 } }}>
                                                                {section.heading && (
                                                                    <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                                                        {section.heading}
                                                                    </Typography>
                                                                )}
                                                                {section.content && section.content.length > 0 && (
                                                                    <Box component="div">
                                                                        {section.content.map((paragraph, pIndex) => (
                                                                            <RichText
                                                                                key={pIndex}
                                                                                html={paragraph}
                                                                                component="div"
                                                                                variant="body2"
                                                                                color="text.secondary"
                                                                                sx={{ mb: 1.5, lineHeight: 1.8, fontSize: { xs: '0.875rem', md: '0.875rem' }, whiteSpace: 'pre-line' }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                                {section.tableData && (
                                                                    <Box sx={{ overflowX: 'auto', mt: 2 }}>
                                                                        <Table sx={{ minWidth: 300 }}>
                                                                            <TableBody>
                                                                                {section.tableData.rows.map((row, rIndex) => (
                                                                                    <TableRow key={rIndex}>
                                                                                        {section.tableData!.columns.map((col, cIndex) => (
                                                                                            <TableCell
                                                                                                key={cIndex}
                                                                                                sx={{
                                                                                                    borderBottom: rIndex < section.tableData!.rows.length - 1 ? 1 : 0,
                                                                                                    borderColor: 'grey.200',
                                                                                                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                                                                                                    py: { xs: 1, md: 1.5 }
                                                                                                }}
                                                                                            >
                                                                                                {row[col] || ''}
                                                                                            </TableCell>
                                                                                        ))}
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </AccordionDetails>
                                                </Accordion>
                                            )
                                        })}
                                    </Box>
                                ) : (
                                    // Desktop: Tabs style
                                    <>
                                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                            <Tabs
                                                value={activeTab || airlineHighlights.tabs[0]?.key || ''}
                                                onChange={(e, newValue) => onTabChange?.(newValue)}
                                                sx={{
                                                    '& .MuiTab-root': {
                                                        textTransform: 'none',
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        minWidth: 'auto',
                                                        px: 3,
                                                        '&.Mui-selected': {
                                                            fontWeight: 600,
                                                            color: 'primary.main'
                                                        }
                                                    },
                                                    '& .MuiTabs-indicator': {
                                                        backgroundColor: 'primary.main',
                                                        height: 2
                                                    }
                                                }}
                                            >
                                                {airlineHighlights.tabs.map(tab => (
                                                    <Tab key={tab.key} label={tab.label} value={tab.key} />
                                                ))}
                                            </Tabs>
                                        </Box>
                                        <Box sx={{ minHeight: 200, mb: 4 }}>
                                            {activeTab && airlineHighlights.tabs.find(tab => tab.key === activeTab) && (
                                                <Box>
                                                    {airlineHighlights.tabs
                                                        .find(tab => tab.key === activeTab)
                                                        ?.sections.map((section, sIndex) => (
                                                            <Box key={sIndex} component="div" sx={{ mb: 3 }}>
                                                                {section.heading && (
                                                                    <Typography component="h3" variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                                                        {section.heading}
                                                                    </Typography>
                                                                )}
                                                                {section.content && section.content.length > 0 && (
                                                                    <Box component="div">
                                                                        {section.content.map((paragraph, pIndex) => (
                                                                            <RichText
                                                                                key={pIndex}
                                                                                html={paragraph}
                                                                                component="div"
                                                                                variant="body2"
                                                                                color="text.secondary"
                                                                                sx={{ mb: 1.5, lineHeight: 1.8 }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                                {section.tableData && (
                                                                    <Table sx={{ mt: 2 }}>
                                                                        <TableBody>
                                                                            {section.tableData.rows.map((row, rIndex) => (
                                                                                <TableRow key={rIndex}>
                                                                                    {section.tableData!.columns.map((col, cIndex) => (
                                                                                        <TableCell
                                                                                            key={cIndex}
                                                                                            sx={{
                                                                                                borderBottom: rIndex < section.tableData!.rows.length - 1 ? 1 : 0,
                                                                                                borderColor: 'grey.200'
                                                                                            }}
                                                                                        >
                                                                                            {row[col] || ''}
                                                                                        </TableCell>
                                                                                    ))}
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                )}
                                                            </Box>
                                                        ))}
                                                </Box>
                                            )}
                                        </Box>
                                    </>
                                )}
                                <Divider sx={{ my: { xs: 3, md: 4 } }} />
                            </Box>
                        )}

                        {/* 6. General Information */}
                        {generalInformation && generalInformation.items && generalInformation.items.length > 0 && (
                            <Box component="section" sx={{ mb: { xs: 3, md: 4 } }}>
                                <Typography
                                    component="h2"
                                    variant={isMobile ? 'h6' : 'h5'}
                                    sx={{
                                        fontWeight: 700,
                                        mb: { xs: 2, md: 3 },
                                        pb: { xs: 1.5, md: 2 },
                                        borderBottom: 2,
                                        borderColor: 'primary.main',
                                        fontSize: { xs: '1.125rem', md: '1.5rem' }
                                    }}
                                >
                                    {t('airlineInformation')}
                                </Typography>
                                <Box sx={{ overflowX: 'auto', mb: { xs: 3, md: 4 } }}>
                                    <Table sx={{ minWidth: 300 }}>
                                        <TableBody>
                                            {generalInformation.items.map((info, index) => (
                                                <TableRow key={index}>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 600,
                                                            width: { xs: '50%', md: '40%' },
                                                            borderBottom: index < generalInformation.items.length - 1 ? 1 : 0,
                                                            borderColor: 'grey.200',
                                                            fontSize: { xs: '0.875rem', md: '1rem' },
                                                            py: { xs: 1, md: 1.5 }
                                                        }}
                                                    >
                                                        {info.label}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            borderBottom: index < generalInformation.items.length - 1 ? 1 : 0,
                                                            borderColor: 'grey.200',
                                                            fontSize: { xs: '0.875rem', md: '1rem' },
                                                            py: { xs: 1, md: 1.5 }
                                                        }}
                                                    >
                                                        {info.value}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                                <Divider sx={{ my: { xs: 3, md: 4 } }} />
                            </Box>
                        )}

                        {/* 7. Popular Airline Destinations */}
                        {popularAirlineDestinations && popularAirlineDestinations.content && popularAirlineDestinations.content.length > 0 && (
                            <Box component="section" sx={{ mb: { xs: 3, md: 4 } }}>
                                {popularAirlineDestinations.title && (
                                    <Typography
                                        component="h2"
                                        variant={isMobile ? 'h6' : 'h5'}
                                        sx={{
                                            fontWeight: 700,
                                            mb: { xs: 2, md: 3 },
                                            pb: { xs: 1.5, md: 2 },
                                            borderBottom: 2,
                                            borderColor: 'primary.main',
                                            fontSize: { xs: '1.125rem', md: '1.5rem' }
                                        }}
                                    >
                                        {popularAirlineDestinations.title}
                                    </Typography>
                                )}
                                <Box component="div" sx={{ mb: { xs: 3, md: 4 } }}>
                                    {popularAirlineDestinations.content.length > 0 && (
                                        <RichText
                                            html={popularAirlineDestinations.content[0]}
                                            component="div"
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 2, lineHeight: 1.8, fontSize: { xs: '0.875rem', md: 14 } }}
                                        />
                                    )}
                                    {popularAirlineDestinations?.destinations?.map((destination, index) => (
                                        <Link
                                            key={index}
                                            href={destination.url}
                                            variant="body1"
                                            color="link"
                                            sx={{
                                                mb: 2,
                                                lineHeight: 1.8,
                                                fontSize: { xs: '0.875rem', md: '1rem' },
                                                display: 'block',
                                                textDecoration: 'none'
                                            }}
                                        >
                                            {destination.text}
                                        </Link>
                                    ))}
                                </Box>
                                <Divider sx={{ my: { xs: 3, md: 4 } }} />
                            </Box>
                        )}

                        {/* 8. FAQ Section */}
                        {faq && faq.items && faq.items.length > 0 && (
                            <Box component="section" sx={{ mb: { xs: 3, md: 4 } }}>
                                {faq.title && (
                                    <Typography
                                        component="h2"
                                        variant={isMobile ? 'h6' : 'h5'}
                                        sx={{
                                            fontWeight: 700,
                                            mb: { xs: 2, md: 3 },
                                            pb: { xs: 1.5, md: 2 },
                                            borderBottom: 2,
                                            borderColor: 'primary.main',
                                            fontSize: { xs: '1.125rem', md: '1.5rem' }
                                        }}
                                    >
                                        {faq.title}
                                    </Typography>
                                )}
                                <Box component="div" itemScope itemType="https://schema.org/FAQPage" sx={{ mb: { xs: 3, md: 4 } }}>
                                    {faq.items.map((faqItem, index) => (
                                        <Accordion
                                            key={index}
                                            component="div"
                                            itemProp="mainEntity"
                                            itemScope
                                            itemType="https://schema.org/Question"
                                            sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Typography component="h3" variant="body1" itemProp="name" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                                    {faqItem.question}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                                                <RichText html={faqItem.answer} component="div" variant="body2" itemProp="text" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: { xs: '0.875rem', md: '0.875rem' } }} />
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Box>
                                <Divider sx={{ my: { xs: 3, md: 4 } }} />
                            </Box>
                        )}

                        {/* 9. Historic Milestones */}
                        {historicMilestones && historicMilestones.milestones && historicMilestones.milestones.length > 0 && (
                            <Box component="section" sx={{ mb: { xs: 3, md: 4 } }}>
                                {historicMilestones.title && (
                                    <Typography
                                        component="h2"
                                        variant={isMobile ? 'h6' : 'h5'}
                                        sx={{
                                            fontWeight: 700,
                                            mb: { xs: 3, md: 4 },
                                            pb: { xs: 1.5, md: 2 },
                                            borderBottom: 2,
                                            borderColor: 'primary.main',
                                            fontSize: { xs: '1.125rem', md: '1.5rem' }
                                        }}
                                    >
                                        {historicMilestones.title}
                                    </Typography>
                                )}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        overflowX: 'auto',
                                        gap: { xs: 2, md: 3 },
                                        pb: 2,
                                        '&::-webkit-scrollbar': {
                                            height: 8
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: 'grey.400',
                                            borderRadius: 4
                                        }
                                    }}
                                >
                                    {historicMilestones.milestones.map((milestone, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                minWidth: { xs: 280, md: 300 },
                                                maxWidth: { xs: 280, md: 300 },
                                                width: { xs: 280, md: 300 },
                                                position: 'relative',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            {/* Green circle with year - centered on top border */}
                                            {milestone.year && (
                                                <Box
                                                    sx={{
                                                        width: { xs: 60, md: 80 },
                                                        height: { xs: 60, md: 80 },
                                                        borderRadius: '50%',
                                                        bgcolor: 'success.main',
                                                        color: 'common.white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: 700,
                                                        fontSize: { xs: '1rem', md: '1.25rem' },
                                                        mb: { xs: -3, md: -4 },
                                                        zIndex: 1,
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: '50%',
                                                        transform: 'translateX(-50%)'
                                                    }}
                                                >
                                                    {milestone.year}
                                                </Box>
                                            )}
                                            {/* Card */}
                                            <Card
                                                sx={{
                                                    width: '100%',
                                                    height: { xs: 250, md: 300 },
                                                    bgcolor: 'common.white',
                                                    border: '1px solid',
                                                    borderColor: 'grey.200',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    pt: { xs: 4, md: 5 },
                                                    mt: { xs: 3, md: 4 }
                                                }}
                                            >
                                                <CardContent
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        flex: 1,
                                                        overflow: 'hidden',
                                                        p: { xs: 1.5, md: 2 },
                                                        alignItems: 'center',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {/* Heading (year again) */}
                                                    {milestone.year && (
                                                        <Typography
                                                            component="h3"
                                                            variant={isMobile ? 'subtitle1' : 'h6'}
                                                            sx={{
                                                                fontWeight: 600,
                                                                mb: 2,
                                                                color: 'text.primary',
                                                                fontSize: { xs: '1rem', md: '1.25rem' }
                                                            }}
                                                        >
                                                            {milestone.title || milestone.year}
                                                        </Typography>
                                                    )}
                                                    {/* Scrollable text content */}
                                                    {milestone.description && milestone.description.length > 0 && (
                                                        <Box
                                                            component="div"
                                                            sx={{
                                                                flex: 1,
                                                                overflowY: 'auto',
                                                                width: '100%',
                                                                px: 1,
                                                                '&::-webkit-scrollbar': {
                                                                    width: 6
                                                                },
                                                                '&::-webkit-scrollbar-thumb': {
                                                                    backgroundColor: 'grey.400',
                                                                    borderRadius: 3
                                                                },
                                                                '&::-webkit-scrollbar-track': {
                                                                    backgroundColor: 'transparent'
                                                                }
                                                            }}
                                                        >
                                                            {Array.isArray(milestone.description)
                                                                ? milestone.description.map((desc, i) => (
                                                                    <RichText
                                                                        key={i}
                                                                        html={desc}
                                                                        component="div"
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        sx={{ mb: 1.5, lineHeight: 1.7, textAlign: 'center', fontSize: { xs: '0.875rem', md: '0.875rem' } }}
                                                                    />
                                                                ))
                                                                : <RichText
                                                                    html={milestone.description}
                                                                    component="div"
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{ mb: 1.5, lineHeight: 1.7, textAlign: 'center', fontSize: { xs: '0.875rem', md: '0.875rem' } }}
                                                                />
                                                            }
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    )
}

export { AirlineDetail }
export type { AirlineDetailProps }
